"use client";

import { submitOrganizerContact } from "@/actions/organizer-contact";
import { useAuth } from "@/contexts/AuthContext";
import { Button, Spinner, Textarea } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Icon } from "@iconify/react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";

const MAX_MESSAGE_LENGTH = 4000;

export function ContactClient({
  tournamentSlug,
  isParticipant,
}: {
  tournamentSlug: string;
  isParticipant: boolean;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const { user, loading } = useAuth();

  const [message, setMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [isPending, startTransition] = useTransition();

  const trimmed = message.trim();
  const isTooLong = message.length > MAX_MESSAGE_LENGTH;
  const isEmpty = trimmed.length === 0;
  const isInvalid = showError && (isEmpty || isTooLong);

  // Loading auth state
  if (loading) {
    return (
      <div className="flex justify-center py-12" aria-busy="true">
        <Spinner color="warning" label={t("common.loading")} />
      </div>
    );
  }

  // Signed-out prompt — the form requires an authenticated player.
  if (!user) {
    const redirectTarget = `/${locale}/tournaments/${tournamentSlug}/contact`;
    return (
      <section
        aria-labelledby="contact-signin-heading"
        className="w-full rounded-xl border border-amber-500/20 bg-[#0f1219] p-6 text-center"
      >
        <Icon
          icon="solar:lock-keyhole-minimalistic-bold"
          className="mx-auto mb-3 text-3xl text-amber-400"
        />
        <h2
          id="contact-signin-heading"
          className="mb-2 text-lg font-semibold text-white"
        >
          {t("contact.signInPrompt")}
        </h2>
        <Button
          className="mt-2 min-h-11 bg-gradient-to-r from-amber-600 to-amber-500 font-medium text-white hover:from-amber-500 hover:to-amber-400"
          onPress={() =>
            router.push(
              `/${locale}/login?redirect=${encodeURIComponent(redirectTarget)}`,
            )
          }
        >
          {t("contact.signInCta")}
        </Button>
      </section>
    );
  }

  // Participants only — signed in but not on a team in this tournament.
  if (!isParticipant) {
    return (
      <section
        aria-labelledby="contact-participants-heading"
        className="w-full rounded-xl border border-amber-500/20 bg-[#0f1219] p-6 text-center"
      >
        <Icon
          icon="solar:users-group-rounded-bold"
          className="mx-auto mb-3 text-3xl text-amber-400"
        />
        <h2
          id="contact-participants-heading"
          className="mb-2 text-lg font-semibold text-white"
        >
          {t("contact.notParticipantTitle")}
        </h2>
        <p className="text-sm text-white/60">
          {t("contact.errors.notParticipant")}
        </p>
      </section>
    );
  }

  const handleSubmit = () => {
    if (isEmpty || isTooLong) {
      setShowError(true);
      return;
    }

    startTransition(async () => {
      const result = await submitOrganizerContact({
        tournamentSlug,
        message: trimmed,
      });

      if (result.success) {
        addToast({ title: t("contact.success"), color: "success" });
        setMessage("");
        setShowError(false);
      } else {
        addToast({
          title: result.error || t("common.unknownError"),
          color: "danger",
        });
      }
    });
  };

  const errorMessage = isEmpty
    ? t("contact.errors.empty")
    : isTooLong
      ? t("contact.errors.tooLong")
      : undefined;

  return (
    <section aria-labelledby="contact-form-heading" className="w-full space-y-4">
      <h2 id="contact-form-heading" className="sr-only">
        {t("contact.title")}
      </h2>

      <Textarea
        label={t("contact.messageLabel")}
        labelPlacement="outside"
        placeholder={t("contact.messagePlaceholder")}
        value={message}
        onValueChange={(value) => {
          setMessage(value);
          if (showError) setShowError(false);
        }}
        minRows={10}
        maxRows={20}
        maxLength={MAX_MESSAGE_LENGTH}
        isRequired
        isInvalid={isInvalid}
        errorMessage={isInvalid ? errorMessage : undefined}
        isDisabled={isPending}
        classNames={{
          inputWrapper:
            "bg-[#0a0c10]/60 border border-amber-500/10 hover:border-amber-500/30 focus-within:border-amber-500/50",
          input: "text-white/80 placeholder:text-white/30",
          label: "text-amber-500/80",
        }}
      />

      <div className="flex justify-end">
        <Button
          onPress={handleSubmit}
          isLoading={isPending}
          isDisabled={isEmpty || isTooLong}
          className="min-h-11 bg-gradient-to-r from-amber-600 to-amber-500 font-medium text-white hover:from-amber-500 hover:to-amber-400 disabled:opacity-50"
          startContent={
            !isPending ? (
              <Icon icon="solar:plain-bold" width={18} />
            ) : undefined
          }
        >
          {t("contact.submit")}
        </Button>
      </div>
    </section>
  );
}
